import cn from 'classnames';
import { BiasType } from 'libs/end-to-end/src/bias-type';
import { getTotalTestCount } from 'libs/end-to-end/src/tests-utils';
import { TestCount } from 'libs/end-to-end/src/tests-utils';
import { Treatment } from 'libs/end-to-end/src/treatment';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { Test } from 'libs/end-to-end/src/tests-utils';
import { isTreatmentStatusSelected } from 'libs/end-to-end/src/treatment';
import { getTestCounts } from 'libs/end-to-end/src/tests-utils';
import React, { useEffect, useMemo, useState } from 'react';
import { BIAS_TYPE_MESSAGE_IDS } from 'apps/sigma2-ui/src/constants/data';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { TreatmentVideosModal } from 'apps/sigma2-ui/src/components/TreatmentVideosModal';
import { TabsRadioGroup } from 'apps/sigma2-ui/src/components/ui/TabsRadioSliderGroup';
import { TreatmentSelectionModal } from 'apps/sigma2-ui/src/components/TreatmentSelectionModal';
import { useToggle } from 'apps/sigma2-ui/src/lib/hooks/useToggle';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { topTreatments } from 'apps/sigma2-ui/src/models/user';
import { ExplainerVideoLink } from 'apps/sigma2-ui/src/components/focus-area/ExplainerVideoLink';
import { getCopilotStylesToDisplay } from 'apps/sigma2-ui/src/components/focus-area/CopilotCardStackType';
import { CopilotCardStack } from 'apps/sigma2-ui/src/components/focus-area/CopilotCardStack';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Pill } from 'apps/sigma2-ui/src/components/Pill';
import { FocusAreaSolutionsCard } from 'apps/sigma2-ui/src/focus-area/FocusAreaSolutionsCard';
import { FocusAreaSolutionsCardProps } from 'apps/sigma2-ui/src/focus-area/FocusAreaSolutionsCard';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';

export interface SolutionsSectionTabData {
  biasType: BiasType;
  failedTestsCount: number;
  label: string;
}

interface TreatmentVideosOverlay {
  failedTests: Test[];
}

interface SolutionsSectionProps {
  focusArea: MyFocusAreaDefinition;
  placeholder?: boolean;
}

const getLevelTreatments = ({
  treatments,
  focusArea,
  dimensionKey,
  levelKey,
  biasType,
}: {
  treatments: Treatment[];
  focusArea: FocusArea;
  dimensionKey: string | undefined;
  levelKey: string | undefined;
  biasType: BiasType;
}) => {
  return treatments.filter(
    (treatment: Treatment) =>
      treatment.focusArea === focusArea &&
      treatment.biasType === biasType &&
      treatment.level.toLowerCase() === levelKey?.toLowerCase() &&
      treatment.dimension.toLowerCase() === dimensionKey?.toLowerCase(),
  );
};

const getLevelFailedTests = ({
  tests,
  focusArea,
  dimensionKey,
  levelKey,
  biasType,
}: {
  tests: Test[];
  focusArea: FocusArea;
  dimensionKey: string | undefined;
  levelKey: string | undefined;
  biasType: BiasType;
}) => {
  return tests.filter(
    (test: Test) =>
      test.focusArea === focusArea &&
      test.biasType === biasType &&
      test.level.toLowerCase() === levelKey?.toLowerCase() &&
      test.dimension.toLowerCase() === dimensionKey?.toLowerCase() &&
      test.status === 'Fail',
  );
};

const getTestOverallStatus = (testCounts: TestCount) => {
  if (testCounts.failing > 0) {
    return 'failed';
  }

  if (testCounts.incomplete > 0) {
    return 'incomplete';
  }

  return 'passing';
};

export const FocusAreaSolutionsSection = ({
  focusArea,
}: SolutionsSectionProps) => {
  const language = useLanguage();
  const { myUser } = useUser();

  const { myObservation, updateTreatments } = useObservation();

  const { allDimensions, getDimensionByColumnHeaderValue } =
    useDimensionConfig();

  const [selectedBiasType, setSelectedBiasType] = useState<BiasType>(
    BiasType.Values.Information,
  );
  const [selectedLevelKey, setSelectedLevelKey] = useState<
    string | undefined
  >();
  const [selectedDimensionKey, setSelectedDimensionKey] = useState<
    string | undefined
  >();

  const [treatmentVideosOverlay, setTreatmentVideosOverlay] =
    useState<TreatmentVideosOverlay | null>(null);

  const focusAreaTests = useMemo(() => {
    if (
      !myUser ||
      !myUser.organization ||
      !myObservation ||
      !myObservation.testSummary
    ) {
      return null;
    }

    return myObservation.testSummary.tests.filter(
      (test) => test.focusArea === focusArea.focusArea,
    );
  }, [myUser, myObservation, focusArea]);

  const validBiasTypes: BiasType[] = useMemo(() => {
    const firstStructural =
      myObservation?.testSummary?.tests.find(
        (test) =>
          test.focusArea === focusArea.focusArea &&
          test.biasType === 'Structural',
      ) ?? null;

    const firstPreference =
      myObservation?.testSummary?.tests.find(
        (test) =>
          test.focusArea === focusArea.focusArea &&
          test.biasType === 'Preference',
      ) ?? null;

    const firstInformation =
      myObservation?.testSummary?.tests.find(
        (test) =>
          test.focusArea === focusArea.focusArea &&
          test.biasType === 'Information',
      ) ?? null;

    const result: BiasType[] = [];

    if (firstStructural) {
      result.push(BiasType.Values.Structural);
    }

    if (firstPreference) {
      result.push(BiasType.Values.Preference);
    }

    if (firstInformation) {
      result.push(BiasType.Values.Information);
    }

    return result;
  }, [myObservation?.testSummary?.tests, focusArea]);

  const tabValues = useMemo(() => {
    const result: {
      tabValue: string;
      automationTag: string;
    }[] = [];

    if (validBiasTypes.includes(BiasType.Values.Information)) {
      result.push({
        tabValue: 'Information',
        automationTag: 'biasTypeTabInformation',
      });
    }

    if (validBiasTypes.includes(BiasType.Values.Preference)) {
      result.push({
        tabValue: 'Preference',
        automationTag: 'biasTypeTabPreference',
      });
    }

    if (validBiasTypes.includes(BiasType.Values.Structural)) {
      result.push({
        tabValue: 'Structural',
        automationTag: 'biasTypeTabStructural',
      });
    }

    return result;
  }, [validBiasTypes]);

  const analytics = useAnalytics();

  useEffect(() => {
    analytics.trackEvent({
      eventName: 'Focus Area Page Viewed',
      eventProperties: {
        selectedFocusArea: focusArea.focusArea,
        selectedTab: 'Solutions',
      },
    });
  }, [analytics, focusArea]);

  useEffect(() => {
    if (!validBiasTypes.includes(selectedBiasType)) {
      setSelectedBiasType(validBiasTypes[0]);
    }
  }, [selectedBiasType, validBiasTypes]);

  const [
    treatmentSelectionModalVisible,
    toggleTreatTreatmentSelectionModalVisible,
  ] = useToggle();

  const focusAreaName = useMemo(() => {
    return focusArea.displayName;
  }, [focusArea]);

  const dimensionDefinition = getDimensionByColumnHeaderValue(
    selectedDimensionKey || '',
  );

  const selectedLevelDefinition = dimensionDefinition?.levels.find((level) => {
    return level.columnValue === selectedLevelKey;
  });

  const levelName = selectedLevelDefinition?.displayName ?? '';

  const cards = useMemo(() => {
    if (!focusAreaTests || !myObservation || !myObservation.treatments) {
      return null;
    }

    const testsForSelectedBiasType = focusAreaTests.filter(
      (test) =>
        test.focusArea === focusArea.focusArea &&
        test.biasType === selectedBiasType,
    );

    const result: React.ReactNode[] = [];
    const solutionCards: FocusAreaSolutionsCardProps[] = [];

    for (const dimension of allDimensions) {
      for (const levelDefinition of dimension.levels) {
        const testsForLevel = testsForSelectedBiasType.filter(
          (test) =>
            test.level.toLowerCase() ===
            levelDefinition.columnValue.toLowerCase(),
        );

        const testCountsForLevel = getTestCounts(testsForLevel);

        // Should we show the card for this dimension?
        if (getTotalTestCount(testCountsForLevel) === 0) {
          continue;
        }

        const overallStatus = getTestOverallStatus(testCountsForLevel);

        // Subset of level card's treatment options
        const levelTreatments = getLevelTreatments({
          treatments: myObservation.treatments,
          focusArea: focusArea.focusArea,
          dimensionKey: dimension.columnHeaderValue.toLowerCase(),
          levelKey: levelDefinition.columnValue.toLowerCase(),
          biasType: selectedBiasType,
        });

        const selectedTreatments = levelTreatments.filter((treatment) => {
          return isTreatmentStatusSelected(treatment.status);
        });

        const treatmentsCount = levelTreatments.length;

        const biasTypeName =
          language[BIAS_TYPE_MESSAGE_IDS[selectedBiasType]]();

        const failedTests = getLevelFailedTests({
          tests: focusAreaTests,
          focusArea: focusArea.focusArea,
          dimensionKey: dimension.columnHeaderValue.toLowerCase(),
          levelKey: levelDefinition.columnValue.toLowerCase(),
          biasType: selectedBiasType,
        });

        const translationParams = {
          focusAreaName: focusAreaName.toLowerCase(),
          biasTypeName: biasTypeName.toLowerCase(),
          calibrationFraming: myObservation.calibrationFraming,
        };

        const incompleteBlurb: TranslatedContent =
          language.incompleteTestBlurb(translationParams);
        const failedBlurb: TranslatedContent =
          language.failedTestBlurb(translationParams);
        const passingBlurb: TranslatedContent =
          language.passingTestBlurb(translationParams);

        const blurb =
          overallStatus === 'incomplete'
            ? incompleteBlurb
            : overallStatus === 'failed'
              ? failedBlurb
              : passingBlurb;

        solutionCards.push({
          levelDefinition,
          testCountsForLevel,
          displayName: dimension.displayName || '',
          selectedTreatments,
          summary: <S2Typography variant="body-7" translatedContent={blurb} />,
          failedTests,
          treatmentsCount,
          onLearnMoreClick: setTreatmentVideosOverlay,
          onTreatmentsCtaClick: () => {
            toggleTreatTreatmentSelectionModalVisible();
            setSelectedLevelKey(levelDefinition.columnValue);
            setSelectedDimensionKey(dimension.columnHeaderValue);
          },
        });
      }
    }

    solutionCards.sort((a, b) => {
      return b.testCountsForLevel.failing - a.testCountsForLevel.failing;
    });

    for (const card of solutionCards) {
      result.push(
        <FocusAreaSolutionsCard
          key={card.levelDefinition.columnValue}
          {...card}
        />,
      );
    }

    return result;
  }, [
    focusAreaTests,
    myObservation,
    focusArea,
    selectedBiasType,
    allDimensions,
    language,
    focusAreaName,
    toggleTreatTreatmentSelectionModalVisible,
  ]);

  const levelTreatments = topTreatments({
    myObservation,
    focusArea: focusArea.focusArea,
    dimensionKey: selectedDimensionKey,
    levelKeys: [selectedLevelKey].filter(Boolean) as string[],
    biasType: selectedBiasType,
  });

  const recommendedTreatments = levelTreatments.slice(0, 3);

  const additionalTreatments = levelTreatments
    /**
     * TODO change this logic so we don't show treatments that aren't recommended. Intentional for now though visually to have
     * the top row show treatments with badges and the bottom to show treatments without badges
     */
    .filter((t) => t.recommendations?.length === 0)
    .slice(3, 6);

  const levelTests =
    myObservation?.testSummary?.tests.filter(
      (test) =>
        test.focusArea === focusArea.focusArea &&
        test.dimension.toLowerCase() === selectedDimensionKey?.toLowerCase() &&
        test.level.toLowerCase() === selectedLevelKey?.toLowerCase() &&
        test.biasType === selectedBiasType,
    ) ?? [];

  const levelTestsCount = getTestCounts(levelTests);

  const failedTestsCount = levelTestsCount.failing;
  const totalTestsCount = getTotalTestCount(levelTestsCount);

  const copilotStyleOptions = getCopilotStylesToDisplay(
    focusArea.focusArea,
    myObservation,
  );

  if (!myObservation) {
    return <></>;
  }

  return (
    <section
      className="col-span-full font-sans"
      data-qa="selectSolutionsSection"
    >
      <UserAnalysesOverlay onUserAnalysisCancel={() => {}} />

      <Spacing direction="vertical" size="gap-20">
        {copilotStyleOptions.length > 0 && (
          <div className="flex flex-col gap-4">
            <S2Typography
              translatedContent={language.focusAreaSelectTreatmentsHeadingTools()}
              variant="headline-5"
            />

            <div>
              {copilotStyleOptions.map((copilotStyleOption) => {
                return (
                  <div key={copilotStyleOption.title} className="flex-0 w-1/3">
                    <CopilotCardStack styling={copilotStyleOption} />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <Spacing direction="vertical" size="sm">
          <S2Typography
            translatedContent={language.focusAreaSelectTreatmentsHeading()}
            variant="headline-5"
          />

          <div className="max-w-prose">
            <S2Typography
              translatedContent={language.focusAreaSelectTreatmentsBlurb()}
              variant="body-7"
            />
          </div>

          <ExplainerVideoLink />

          <Spacing direction="vertical" size="xl">
            <TabsRadioGroup
              currValue={selectedBiasType}
              values={tabValues}
              onChange={setSelectedBiasType}
              renderTab={({ value, checked }) => {
                if (!focusAreaTests) {
                  return <></>;
                }
                const tabBiasType = BiasType.parse(value.tabValue);

                const testsForSelectedBiasType = focusAreaTests.filter(
                  (test) => test.biasType === tabBiasType,
                );

                const testResultsByBiasType = getTestCounts(
                  testsForSelectedBiasType,
                );

                const tabData = {
                  label: language[BIAS_TYPE_MESSAGE_IDS[tabBiasType]](),
                  failedTestsCount: testResultsByBiasType.failing,
                };

                return (
                  <Spacing
                    direction="horizontal"
                    size="sm"
                    className="items-center"
                  >
                    <S2Typography
                      variant="headline-6-alt"
                      translatedContent={language.dangerousUserInput({
                        value: tabData.label,
                      })}
                      textColorVariant={checked ? 'royal-600' : 'gray-600'}
                    />

                    <Pill
                      bgClassName={cn(
                        { 'bg-gray-1000': checked, 'bg-gray-600': !checked },
                        'uppercase',
                      )}
                    >
                      <S2Typography
                        variant="small-label-alt"
                        translatedContent={language.testsFailed({
                          failedTestsCount: tabData.failedTestsCount,
                        })}
                        textColorVariant="white"
                      />
                    </Pill>
                  </Spacing>
                );
              }}
            />

            {cards?.length ? (
              <div className="grid grid-cols-12 gap-6">{cards}</div>
            ) : (
              <S2Typography
                translatedContent={language.allTestsPassed()}
                variant="body-7"
              />
            )}

            <TreatmentSelectionModal
              show={treatmentSelectionModalVisible}
              topTreatments={{
                treatments: recommendedTreatments,
                title: (
                  <Spacing direction="horizontal" size="sm">
                    <S2Typography
                      variant="headline-5"
                      translatedContent={language.dangerousUserInput({
                        value: `${dimensionDefinition?.displayName}: ${levelName}`,
                      })}
                    />

                    <Pill bgClassName="bg-red-100">
                      <S2Typography
                        variant="small-label-alt"
                        translatedContent={language.treatmentSelectionTestsFailed(
                          {
                            failedTestsCount,
                            totalTestsCount,
                          },
                        )}
                        textColorVariant="red-800"
                      />
                    </Pill>
                  </Spacing>
                ),
                description: (
                  <S2Typography
                    variant="body-7"
                    translatedContent={language.treatmentSelectionBlurb({
                      calibrationFraming: myObservation.calibrationFraming,
                    })}
                  />
                ),
              }}
              additionalTreatments={{
                treatments: additionalTreatments,
                title: (
                  <S2Typography
                    translatedContent={language.focusAreaKeyTakeawayTreatmentSelectionModalMoreSolutionsTitle()}
                    variant="headline-3-alt"
                  />
                ),
              }}
              updateTreatments={updateTreatments}
              onClose={toggleTreatTreatmentSelectionModalVisible}
            />

            {treatmentVideosOverlay && (
              <TreatmentVideosModal
                open={true}
                onClose={() => {
                  setTreatmentVideosOverlay(null);
                }}
                tests={treatmentVideosOverlay.failedTests}
              />
            )}
          </Spacing>
        </Spacing>
      </Spacing>
    </section>
  );
};
